import { Injectable } from '@angular/core';

import { Status, VehicleSource } from '@enums';
import {
  Contract,
  CriminalRecordCheckDocument,
  DocumentType,
  User,
  UserBatch,
} from '@models';
import { CrudService, HttpService } from '@services';
import { environment } from '../../../environment/environment';

@Injectable({ providedIn: 'root' })
export class UserService extends CrudService<User> {
  constructor(http: HttpService) {
    super('user', http);
  }

  public static isUserApproved(
    user: User,
    documentTypes: DocumentType[],
    latestContract: Contract | null,
    latestCriminalRecordCheckDocument: CriminalRecordCheckDocument | null
  ): boolean {
    const isApproved =
      this.isUserProfileApproved(user) &&
      this.isUserAddressApproved(user) &&
      this.areUserDocumentsApproved(user, documentTypes) &&
      this.areUserBankingDetailsApproved(user) &&
      this.isUserVehicleApproved(user) &&
      !!user?.carTrackDriverId &&
      !!user?.contractSignature &&
      user.contractSignature.contractId == latestContract?.id &&
      user.criminalRecordCheck?.criminalRecordCheckDocumentId ==
        latestCriminalRecordCheckDocument?.id &&
      user.criminalRecordCheck?.status === Status.APPROVED;
    return isApproved;
  }

  public static isUserEligibleForVehicle(
    user: User,
    documentTypes: DocumentType[]
  ): boolean {
    return (
      !user.vehicle &&
      this.isUserProfileApproved(user) &&
      this.isUserAddressApproved(user) &&
      this.areUserDocumentsApproved(user, documentTypes) &&
      !!user?.carTrackDriverId
    );
  }

  public static isUserProfileApproved(user: User): boolean {
    return user.profile?.status === Status.APPROVED;
  }

  public static isUserAddressApproved(user: User): boolean {
    return user.address?.status === Status.APPROVED;
  }

  public static areUserBankingDetailsApproved(user: User): boolean {
    return user.bankingDetails?.status === Status.APPROVED;
  }

  public static isUserVehicleApproved(user: User): boolean {
    if (user.vehicle && user.vehicle.source !== VehicleSource.USER) return true;
    return user.vehicle?.status === Status.APPROVED;
  }

  public static areUserDocumentsApproved(
    user: User,
    documentTypes: DocumentType[]
  ): boolean {
    const documents = user.documents;
    if (!documents || !documents.length) return false;

    const requiredDocTypes = documentTypes.filter((x) => x.required);
    for (const docType of requiredDocTypes) {
      const doc = documents.find((x) => x.documentType.id === docType?.id);
      if (!doc || doc.status != Status.APPROVED) return false;
    }

    return true;
  }

  public async createCarTrackProfile(user: User): Promise<any> {
    const endpoint = `${environment.apiUrl}/admin/car-track/profile`;
    const response = await this.http.post(endpoint, {
      userId: user?.id,
    });
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async getBatches(): Promise<UserBatch[]> {
    const response = await this.http.get<UserBatch[]>(`${this.url}/batch/all`);
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async getBatchById(batchId: string): Promise<UserBatch> {
    const response = await this.http.get<UserBatch>(
      `${this.url}/batch/${batchId}`
    );
    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async uploadBatch(file: File): Promise<UserBatch> {
    const response = await this.http.postWithFile<UserBatch>(
      `${this.url}/batch/upload`,
      null,
      file
    );

    if (!response.success) throw new Error(response.message);
    return response.data;
  }

  async processBatch(batchId: string): Promise<void> {
    const response = await this.http.post<void>(
      `${this.url}/batch/process/${batchId}`,
      {}
    );
    if (!response.success) throw new Error(response.message);
    return response.data;
  }
}
